import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, ButtonBase, Chip, MenuItem, Paper, Select, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { reportDashboardApi } from "../../services/api";
import { GROUPS, SECTIONS_META } from "./SectionsMeta";
import { PRESETS, presetRange, addDays } from "./Reportkit";
import { clearReportCache } from "./UseReportData";
import OverviewSection from "./OverviewSection";
import ProductionSection from "./ProductionSection";
import FlowSection from "./FlowSection";
import GradesSection from "./GradesSection";
import EquipmentSection from "./EquipmentSection";
import FuelSection from "./FuelSection";
import SafetyEventsSection from "./SafetyEventsSection";
import VoiceSection from "./VoiceSection";
import ExecutiveSection from "./Executivesection";
import CrewSection from "./Crewsection";

const COMPONENTS = {
  overview: OverviewSection, exec: ExecutiveSection, crew: CrewSection, production: ProductionSection, flow: FlowSection, grades: GradesSection,
  equipment: EquipmentSection, fuel: FuelSection, safety: SafetyEventsSection, voice: VoiceSection,
};

const DEFAULTS = { preset: "l30", start: "", end: "", grain: "auto", shift: "", crews: null, material: "", model: "", side: "" };

export default function Reports() {
  const [section, setSection] = useState("overview");
  const [options, setOptions] = useState(null);
  const [optError, setOptError] = useState(null);
  const [f, setF] = useState(DEFAULTS);

  useEffect(() => {
    reportDashboardApi.options().then(setOptions).catch((e) => { setOptError(e?.message || "Could not load report options"); setOptions({ shifts: [], crews: [], equipmentModels: [], materials: [], dataRange: null }); });
  }, []);

  const range = useMemo(
    () => (f.preset === "custom" ? { start: f.start, end: f.end } : presetRange(f.preset, options?.dataRange)),
    [f.preset, f.start, f.end, options]
  );

  const req = useMemo(() => {
    const allCrews = options?.crews?.length || 0;
    return {
      startDate: range.start, endDate: range.end, grain: f.grain === "auto" ? null : f.grain,
      shift: f.shift || null, crews: f.crews && f.crews.length && f.crews.length < allCrews ? f.crews : [],
      material: f.material || null, equipmentModel: f.model || null, pitSide: f.side || null,
    };
  }, [range, f, options]);

  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  const crewOn = (id) => f.crews == null || f.crews.includes(id);
  const toggleCrew = (id) => {
    const all = (options?.crews || []).map((c) => c.id);
    const cur = new Set(f.crews == null ? all : f.crews);
    if (cur.has(id)) { if (cur.size > 1) cur.delete(id); } else cur.add(id);
    set({ crews: cur.size === all.length ? null : [...cur] });
  };
  const onPreset = (p) => {
    if (p === "custom") set({ preset: "custom", start: range.start, end: range.end });
    else set({ preset: p });
  };
  const reset = () => { clearReportCache(); setF(DEFAULTS); };

  const meta = SECTIONS_META.find((s) => s.id === section);
  const Active = COMPONENTS[section];
  const ready = options != null && range.start && range.end;
  const activeCount = (f.preset !== "l30" ? 1 : 0) + (f.shift ? 1 : 0) + (f.crews ? 1 : 0) + (f.material ? 1 : 0) + (f.model ? 1 : 0) + (f.side ? 1 : 0);

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "230px minmax(0,1fr)" }, gap: 2, alignItems: "start" }}>
      {/* ───── left: report menu ───── */}
      <Paper variant="outlined" sx={{ p: 1, position: { md: "sticky" }, top: { md: 8 } }}>
        <Box sx={{ px: 1, py: 1, mb: 0.5 }}>
          <Typography fontWeight={700} sx={{ lineHeight: 1.1 }}>Reports</Typography>
          <Typography variant="caption" color="text.secondary">MineProd operations</Typography>
        </Box>
        {GROUPS.map((g) => (
          <Box key={g} sx={{ mb: 0.5 }}>
            <Typography sx={{ px: 1, pt: 1, fontSize: 10.5, letterSpacing: 0.9, textTransform: "uppercase", color: "text.secondary", fontWeight: 700 }}>{g}</Typography>
            {SECTIONS_META.filter((s) => s.group === g).map((s) => {
              const Icon = s.icon;
              const active = s.id === section;
              return (
                <ButtonBase key={s.id} disabled={s.soon} onClick={() => setSection(s.id)} title={s.soon ? "Coming next — needs operator-level data" : s.desc}
                  sx={{ display: "flex", width: "100%", justifyContent: "flex-start", gap: 1.25, px: 1, py: 0.9, borderRadius: 1, textAlign: "left",
                    bgcolor: active ? "action.selected" : "transparent", fontWeight: active ? 700 : 500, opacity: s.soon ? 0.5 : 1, "&:hover": { bgcolor: "action.hover" } }}>
                  <Icon sx={{ fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: "inherit", flex: 1 }}>{s.title}</Typography>
                  {s.soon && <Chip size="small" label="Soon" sx={{ height: 18, fontSize: 10 }} />}
                </ButtonBase>
              );
            })}
          </Box>
        ))}
      </Paper>

      {/* ───── right: filters + report ───── */}
      <Box sx={{ minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">PERIOD</Typography>
            <Select size="small" value={f.preset} onChange={(e) => onPreset(e.target.value)} sx={{ minWidth: 170, fontSize: 13 }}>
              {PRESETS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </Select>
            <TextField size="small" type="date" value={range.start || ""} disabled={f.preset !== "custom"} inputProps={{ min: options?.dataRange?.startDate, max: options?.dataRange?.endDate }}
              onChange={(e) => set({ start: e.target.value })} sx={{ width: 150 }} />
            <TextField size="small" type="date" value={range.end || ""} disabled={f.preset !== "custom"} inputProps={{ min: options?.dataRange?.startDate, max: options?.dataRange?.endDate }}
              onChange={(e) => set({ end: e.target.value })} sx={{ width: 150 }} />
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">VIEW</Typography>
            <ToggleButtonGroup size="small" exclusive value={f.grain} onChange={(_, v) => v && set({ grain: v })} sx={{ "& .MuiToggleButton-root": { py: 0.4, px: 1.25, textTransform: "none" } }}>
              {[["auto", "Auto"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]].map(([v, l]) => <ToggleButton key={v} value={v}>{l}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">SHIFT</Typography>
            <ToggleButtonGroup size="small" exclusive value={f.shift} onChange={(_, v) => set({ shift: v ?? "" })} sx={{ "& .MuiToggleButton-root": { py: 0.4, px: 1.25, textTransform: "none" } }}>
              <ToggleButton value="">All</ToggleButton>
              {(options?.shifts || []).map((s) => <ToggleButton key={s} value={s}>{s}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>
          {(options?.crews?.length || 0) > 0 && (
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">CREW</Typography>
              {options.crews.map((c) => (
                <Chip key={c.id} size="small" label={c.name} onClick={() => toggleCrew(c.id)} color={crewOn(c.id) ? "primary" : "default"} variant={crewOn(c.id) ? "filled" : "outlined"} />
              ))}
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">MATERIAL</Typography>
            <Select size="small" displayEmpty value={f.material} onChange={(e) => set({ material: e.target.value })} sx={{ minWidth: 120, fontSize: 13 }}>
              <MenuItem value="">All materials</MenuItem>
              {(options?.materials || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
            <Select size="small" displayEmpty value={f.model} onChange={(e) => set({ model: e.target.value })} sx={{ minWidth: 140, fontSize: 13 }}>
              <MenuItem value="">All equipment models</MenuItem>
              {(options?.equipmentModels || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
            <Select size="small" displayEmpty value={f.side} onChange={(e) => set({ side: e.target.value })} sx={{ minWidth: 130, fontSize: 13 }} title="Pit side applies to production, flow, grades, crew and executive views">
              <MenuItem value="">All pit sides</MenuItem>
              {(options?.pitSides || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </Select>
          </Box>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            {activeCount > 0 && <Typography variant="caption" color="text.secondary">{activeCount} filter{activeCount > 1 ? "s" : ""} active</Typography>}
            <Button size="small" onClick={reset}>Reset filters</Button>
          </Box>
        </Paper>

        {optError && <Alert severity="warning" sx={{ mb: 2 }}>{optError}</Alert>}
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {meta?.title} · {range.start} → {range.end}. Shift date = date the shift starts (Day 06–18, Night 18–06, America/Edmonton). Crew = crew recorded on the dispatch shift.
        </Typography>
        {ready ? <Active key={section} req={req} onNavigate={setSection} /> : null}
      </Box>
    </Box>
  );
}